import {type PropsWithChildren, useCallback, useMemo, useRef, useState} from "react";
import {TableTopSelectionEvents} from "./TableTopSelectionEvents.ts";
import {useCombatants} from "@/encounter/combatants/CombatantHooks.ts";
import {useAreaEffect} from "@/encounter/effect/AreaEffectHooks.ts";
import type Combatant from "@/encounter/model/Combatant.ts";
import type {AreaEffect} from "@/encounter/model/AreaEffect.ts";
import {type TableTopSelection, TableTopSelectionContext,} from "@/tabletop/selection/TableTopSelectionHooks.ts";
import {useDungeon} from "@/encounter/dungeon/DungeonHooks.ts";
import {effectsOnLevel} from "@/tabletop/effects/AreaEffectLevel.ts";

export default function TableTopSelectionProvider({children}: PropsWithChildren) {
    const {combatants} = useCombatants();
    const {effects} = useAreaEffect();
    const {dungeonSettings} = useDungeon();
    const [selection, setSelection] = useState<TableTopSelection>();
    const listeners = useRef(new Set<() => void>());
    const events = useMemo(() => ({subscribe: (listener: () => void) => {
        listeners.current.add(listener);
        return () => {listeners.current.delete(listener);};
    }}), []);
    const commitSelection = useCallback((next: TableTopSelection) => {
        if (next) listeners.current.forEach(listener => listener());
        setSelection(next);
    }, []);

    const selectedCombatantIds = new Set(selection?.type === "combatants" ? selection.ids : []);
    const selectedCombatants = combatants.filter(combatant => selectedCombatantIds.has(combatant.id));
    const selectedEffectId = selection?.type === "areaEffect" ? selection.id : null;
    const selectedEffect = selectedEffectId
        ? effectsOnLevel(effects, combatants, dungeonSettings.levelId)
            .find(effect => effect.id === selectedEffectId)
        : undefined;

    const selectCombatants = (selected: Combatant[]) => {
        commitSelection(selected.length > 0
            ? {type: "combatants", ids: selected.map(combatant => combatant.id)}
            : undefined);
    };
    const selectAreaEffect = (effect: AreaEffect) => commitSelection({type: "areaEffect", id: effect.id});
    const clearSelection = () => setSelection(undefined);

    const isCombatantSelected = (combatant: Combatant) => selectedCombatantIds.has(combatant.id);
    const isAreaEffectSelected = (effect: AreaEffect) => selectedEffect?.id === effect.id;
    const value = {
        selectedCombatants,
        selectedEffect,
        isCombatantSelected,
        isAreaEffectSelected,
        selectCombatants,
        selectAreaEffect,
        clearSelection,
    };

    return (
        <TableTopSelectionEvents.Provider value={events}><TableTopSelectionContext.Provider value={value}>
            {children}
        </TableTopSelectionContext.Provider></TableTopSelectionEvents.Provider>
    );
}
